<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <v-row>
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-4">
              Schedule Run History
              <span v-if="schedule" class="text-h6 font-weight-normal text--secondary">
                - {{ schedule.name }}
              </span>
            </h1>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn dark color="grey" @click="$router.go(-1)">
              <v-icon left class="material-icons-round">arrow_back</v-icon>
              Back to Schedules
            </v-btn>
          </v-col>
        </v-row>

        <!-- Schedule Info Card -->
        <v-row v-if="schedule">
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title>Schedule Information</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <strong>Name:</strong> {{ schedule.name }}
                  </v-col>
                  <v-col cols="12" md="3">
                    <strong>Dealer:</strong> {{ schedule.dealer }}
                  </v-col>
                  <v-col cols="12" md="3">
                    <strong>Frequency:</strong> {{ schedule.frequency }}
                  </v-col>
                  <v-col cols="12" md="3">
                    <strong>Status:</strong>
                    <v-chip :color="schedule.is_active ? 'success' : 'error'" small>
                      {{ schedule.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Run History Table -->
        <v-row>
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title class="d-flex justify-space-between">
                <span>Run History</span>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search runs..."
                  single-line
                  hide-details
                  dense
                  outlined
                  style="max-width: 300px;"
                ></v-text-field>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="runHeaders"
                  :items="runs"
                  :search="search"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="15"
                  sort-by="started_at"
                  sort-desc
                >
                  <template v-slot:item.status="{ item }">
                    <v-chip :color="getRunStatusColor(item.status)" small>
                      {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.started_at="{ item }">
                    {{ formatDate(item.started_at) }}
                  </template>

                  <template v-slot:item.completed_at="{ item }">
                    <span v-if="item.completed_at">
                      {{ formatDate(item.completed_at) }}
                    </span>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.duration="{ item }">
                    <span v-if="item.formatted_duration">
                      {{ item.formatted_duration }}
                    </span>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.vehicles_found="{ item }">
                    <v-chip color="info" small>
                      {{ item.vehicles_found || 0 }}
                    </v-chip>
                  </template>

                  <template v-slot:item.vehicles_processed="{ item }">
                    <span class="text-success">+{{ item.vehicles_inserted || 0 }}</span>
                    <span class="mx-1">/</span>
                    <span class="text-warning">~{{ item.vehicles_updated || 0 }}</span>
                  </template>

                  <template v-slot:item.error_message="{ item }">
                    <v-tooltip bottom v-if="item.error_message" max-width="400">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="error" v-bind="attrs" v-on="on">error</v-icon>
                      </template>
                      <span>{{ item.error_message }}</span>
                    </v-tooltip>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="viewRunDetails(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">visibility</v-icon>
                        </v-btn>
                      </template>
                      <span>View Details</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.vehicles_found > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="viewScrapedVehicles(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">directions_car</v-icon>
                        </v-btn>
                      </template>
                      <span>View Scraped Vehicles</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Run Details Dialog -->
      <v-dialog v-model="detailsDialog" max-width="800px">
        <v-card v-if="selectedRun">
          <v-card-title>
            Run Details - {{ formatDate(selectedRun.started_at) }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Status</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip :color="getRunStatusColor(selectedRun.status)" small>
                          {{ selectedRun.status.charAt(0).toUpperCase() + selectedRun.status.slice(1) }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Started At</v-list-item-title>
                      <v-list-item-subtitle>{{ formatDate(selectedRun.started_at) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedRun.completed_at">
                    <v-list-item-content>
                      <v-list-item-title>Completed At</v-list-item-title>
                      <v-list-item-subtitle>{{ formatDate(selectedRun.completed_at) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedRun.formatted_duration">
                    <v-list-item-content>
                      <v-list-item-title>Duration</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedRun.formatted_duration }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Vehicles Found</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedRun.vehicles_found || 0 }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Vehicles Inserted</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedRun.vehicles_inserted || 0 }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Vehicles Updated</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedRun.vehicles_updated || 0 }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <v-col cols="12" v-if="selectedRun.error_message">
                <v-alert type="error" dense>
                  <strong>Error:</strong> {{ selectedRun.error_message }}
                </v-alert>
              </v-col>
              
              <v-col cols="12" v-if="selectedRun.run_details">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Technical Details</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre>{{ JSON.stringify(selectedRun.run_details, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="detailsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer-admin></vue-footer-admin>
  </v-app>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ScheduleHistory',
  data() {
    return {
      loading: false,
      search: '',
      schedule: null,
      runs: [],
      detailsDialog: false,
      selectedRun: null,

      runHeaders: [
        { text: 'Status', value: 'status' },
        { text: 'Started At', value: 'started_at' },
        { text: 'Completed At', value: 'completed_at' },
        { text: 'Duration', value: 'duration' },
        { text: 'Found', value: 'vehicles_found' },
        { text: 'New/Updated', value: 'vehicles_processed' },
        { text: 'Error', value: 'error_message' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },

  mounted() {
    this.fetchScheduleHistory()
  },

  methods: {
    async fetchScheduleHistory() {
      this.loading = true
      const scheduleId = this.$route.params.id
      
      try {
        const response = await axios.get(`/api/schedules/${scheduleId}/history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        
        if (response.data.success) {
          this.schedule = response.data.schedule
          this.runs = response.data.runs.data || response.data.runs
        }
      } catch (error) {
        console.error('Error fetching schedule history:', error)
        this.$toast.error('Failed to load schedule history')
      } finally {
        this.loading = false
      }
    },

    viewRunDetails(run) {
      this.selectedRun = run
      this.detailsDialog = true
    },

    viewScrapedVehicles(run) {
      this.$router.push(`/admin/scraped-vehicles/${run.id}`)
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY HH:mm:ss')
    },

    getRunStatusColor(status) {
      switch (status) {
        case 'completed': return 'success'
        case 'failed': return 'error'
        case 'started': return 'warning'
        default: return 'grey'
      }
    }
  }
}
</script>
